import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatDateShort } from "../lib/format";
import { Plus, Trash2, Shield, User, X } from "lucide-react";

const EMPTY = { name: "", email: "", password: "", role: "viewer" };

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  const reload = async () => {
    const { data } = await api.get("/team");
    setMembers(data);
  };
  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/team", form);
      setForm(EMPTY); setAdding(false);
      await reload();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (id) => {
    if (!confirm("Remove this team member?")) return;
    await api.delete(`/team/${id}`);
    await reload();
  };

  return (
    <Layout>
      <PageHeader
        overline="Access control"
        title="Team"
        subtitle="Add or remove ISF Ops users. Admins can manage the team; viewers can edit data."
        actions={
          <button
            onClick={() => setAdding(true)}
            data-testid="add-member-btn"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium px-4 py-2 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add member
          </button>
        }
        testId="team-header"
      />

      {adding && (
        <Card className="p-6 mb-8 border-emerald-500/40 max-w-2xl" testId="add-member-form">
          <form onSubmit={create} className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="overline">New member</div>
              <button type="button" onClick={() => { setAdding(false); setForm(EMPTY); setError(""); }} className="text-zinc-500 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="member-name"
                  className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
              </div>
              <div>
                <label className="overline block mb-2">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="member-email"
                  className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
              </div>
              <div>
                <label className="overline block mb-2">Password</label>
                <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="member-password" minLength={6}
                  className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
              </div>
              <div>
                <label className="overline block mb-2">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="member-role"
                  className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500">
                  <option value="viewer">Viewer · view + edit</option>
                  <option value="admin">Admin · full access</option>
                </select>
              </div>
            </div>
            {error && (
              <div data-testid="add-member-error" className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2">{error}</div>
            )}
            <button type="submit" data-testid="save-member"
              className="bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium px-5 py-2 transition-colors">
              Create member
            </button>
          </form>
        </Card>
      )}

      <Card className="overflow-hidden" testId="team-table-card">
        <table className="w-full text-sm">
          <thead className="bg-[#181818]">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-4 py-3 font-medium">Member</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              const self = m.id === user.id;
              return (
                <tr key={m.id} className="border-t border-zinc-900 hover:bg-[#161616]" data-testid={`member-${m.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 grid place-items-center bg-[#181818] border border-zinc-800 text-emerald-400 font-display font-semibold">
                        {m.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium">{m.name}{self && <span className="ml-2 text-xs text-emerald-400">· you</span>}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{m.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.18em] border rounded-sm ${
                      m.role === "admin"
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                        : "bg-zinc-500/15 text-zinc-400 border-zinc-500/20"
                    }`}>
                      {m.role === "admin" ? <Shield className="h-3 w-3" /> : <User className="h-3 w-3" />}
                      {m.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{formatDateShort(m.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      disabled={self}
                      onClick={() => remove(m.id)}
                      data-testid={`remove-${m.id}`}
                      className="p-1.5 text-red-400 hover:bg-[#1a1a1a] disabled:opacity-30 disabled:cursor-not-allowed"
                      title={self ? "Cannot remove yourself" : "Remove member"}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </Layout>
  );
}
